import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, Home, Building2, Flame, AlertTriangle } from "lucide-react";
import { Reveal, SectionLabel } from "@/lib/Reveal";
import { API_BASE } from "@/lib/data";

const Chip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`border rounded-sm px-4 py-3 font-display font-semibold text-sm transition-all ${
      active ? "border-torch bg-torch/10 text-white" : "border-neutral-800 bg-surface text-neutral-400 hover:border-neutral-600"
    }`}
  >
    {children}
  </button>
);

export const Quote = () => {
  const [property, setProperty] = useState("house");
  const [boiler, setBoiler] = useState("combi");
  const [urgency, setUrgency] = useState("this_week");
  const [form, setForm] = useState({ name: "", phone: "", email: "", postcode: "", message: "" });
  const [loading, setLoading] = useState(false);

  const estimate = () => {
    if (urgency === "emergency") return "£110 – £260";
    if (boiler === "system") return property === "house" ? "£120 – £320" : "£99 – £240";
    return property === "house" ? "£99 – £280" : "£99 – £220";
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please add your name, phone and email so we can come back to you");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/quotes`, {
        ...form,
        property_type: property,
        boiler_type: boiler,
        urgency,
        estimate: estimate(),
      });
      toast.success(`Quote request ${data.ref} received — we'll call you back shortly`);
      setForm({ name: "", phone: "", email: "", postcode: "", message: "" });
    } catch (err) {
      toast.error("Could not send your request — please call us instead");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-[#0E0E0E] border border-neutral-800 focus:border-torch outline-none rounded-sm px-4 py-3 text-sm text-white placeholder:text-neutral-600 transition-colors";

  return (
    <section id="quote" data-testid="quote-section" className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <Reveal><SectionLabel>05 / Free quote</SectionLabel></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white mt-4">
              Bigger job?<br />Get a free quote.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-neutral-400 leading-relaxed max-w-md">
              New boiler, full heating system, or something unusual — tell us what you need
              and get an instant ballpark. A real engineer then calls you back with an exact,
              no-obligation price.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 border border-torch/30 bg-torch/5 rounded-sm p-6" data-testid="quote-estimate-card">
              <p className="font-mono text-[10px] tracking-[0.25em] text-torch uppercase">Instant ballpark</p>
              <p data-testid="quote-estimate-value" className="font-mono font-bold text-4xl text-white mt-2">{estimate()}</p>
              <p className="text-xs text-neutral-500 mt-2">Based on your selections — exact quote confirmed by an engineer.</p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="space-y-6 bg-surface border border-neutral-800 rounded-sm p-6 sm:p-8" data-testid="quote-form">
            <div>
              <p className="font-mono text-xs tracking-[0.25em] text-neutral-500 uppercase mb-3 flex items-center gap-2"><Home className="w-3.5 h-3.5" /> Property</p>
              <div className="flex gap-3">
                <Chip active={property === "house"} onClick={() => setProperty("house")} testId="quote-property-house">House</Chip>
                <Chip active={property === "flat"} onClick={() => setProperty("flat")} testId="quote-property-flat">Flat / Apartment</Chip>
              </div>
            </div>
            <div>
              <p className="font-mono text-xs tracking-[0.25em] text-neutral-500 uppercase mb-3 flex items-center gap-2"><Flame className="w-3.5 h-3.5" /> Boiler type</p>
              <div className="flex gap-3 flex-wrap">
                <Chip active={boiler === "combi"} onClick={() => setBoiler("combi")} testId="quote-boiler-combi">Combi</Chip>
                <Chip active={boiler === "system"} onClick={() => setBoiler("system")} testId="quote-boiler-system">System</Chip>
                <Chip active={boiler === "unsure"} onClick={() => setBoiler("unsure")} testId="quote-boiler-unsure">Not sure</Chip>
              </div>
            </div>
            <div>
              <p className="font-mono text-xs tracking-[0.25em] text-neutral-500 uppercase mb-3 flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5" /> How urgent?</p>
              <div className="flex gap-3 flex-wrap">
                <Chip active={urgency === "emergency"} onClick={() => setUrgency("emergency")} testId="quote-urgency-emergency">Emergency</Chip>
                <Chip active={urgency === "this_week"} onClick={() => setUrgency("this_week")} testId="quote-urgency-week">This week</Chip>
                <Chip active={urgency === "flexible"} onClick={() => setUrgency("flexible")} testId="quote-urgency-flexible">Flexible</Chip>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="quote-name-input" className={inputCls} placeholder="Full name" value={form.name} onChange={set("name")} />
              <input data-testid="quote-phone-input" className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="quote-email-input" type="email" className={inputCls} placeholder="Email address" value={form.email} onChange={set("email")} />
              <input data-testid="quote-postcode-input" className={inputCls} placeholder="Postcode" value={form.postcode} onChange={set("postcode")} />
            </div>
            <textarea
              data-testid="quote-message-input"
              className={`${inputCls} resize-none`}
              rows={3}
              placeholder="Describe the job or the problem…"
              value={form.message}
              onChange={set("message")}
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="quote-submit-btn"
              className="w-full bg-torch hover:bg-torch-hover disabled:opacity-60 text-white font-display font-bold uppercase tracking-wide py-4 rounded-sm flex items-center justify-center gap-3 transition-all hover:shadow-[0_0_32px_rgba(255,85,0,0.45)]"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" />}
              {loading ? "Sending…" : "Request my free quote"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
